// src/pages/Convocations/ConvocationByEventPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import {
  getAllActorsByEventId,
  toggleActeurValidation,
} from "./../../services/acteureventServices";
import { getAllEvents } from "./../../services/eventServices";
import NoteModal from "./NoteModal";
import Button from "../../components/Button/Button";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";

/* ===== Helpers ===== */

const ROLE_SECTIONS = [
  {
    key: "Organisateur",
    label: "Organisateurs",
    description: "Direction générale de la manifestation.",
    tone: "border-amber-200 bg-amber-50/70",
  },
  {
    key: "ResponsableComite",
    label: "Responsables Comité",
    description: "Référents Comité, coordination globale.",
    tone: "border-sky-200 bg-sky-50/70",
  },
  {
    key: "Benevolontaire",
    label: "Bénévolontaires",
    description: "Accueil, logistique, buvette, etc.",
    tone: "border-emerald-200 bg-emerald-50/70",
  },
  {
    key: "ResponsableArbitrage",
    label: "Responsables Arbitrage",
    description: "Pilotage de l’arbitrage sur la journée.",
    tone: "border-indigo-200 bg-indigo-50/70",
  },
  {
    key: "Notateur",
    label: "Notateurs",
    description: "Gestion des feuilles, informatique, tables.",
    tone: "border-violet-200 bg-violet-50/70",
  },
  {
    key: "Arbitre",
    label: "Arbitres",
    description: "Arbitrage sur les surfaces.",
    tone: "border-orange-200 bg-orange-50/70",
  },
  {
    key: "CommissaireSportif",
    label: "Commissaires Sportifs",
    description: "Gestion des tables, annonces, appels.",
    tone: "border-rose-200 bg-rose-50/70",
  },
  {
    key: "Juge",
    label: "Juges",
    description: "Juges techniques (kata, etc.).",
    tone: "border-slate-200 bg-slate-50/70",
  },
];

const initialActorsState = {
  Organisateur: [],
  ResponsableComite: [],
  Benevolontaire: [],
  ResponsableArbitrage: [],
  Notateur: [],
  Juge: [],
  Arbitre: [],
  CommissaireSportif: [],
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateRange = (start, end) => {
  if (!start) return "-";
  const dStart = formatDate(start);
  if (!end || end === "0000-00-00") return dStart;
  const dEnd = formatDate(end);
  return `${dStart}  →  ${dEnd}`;
};

const ConvocationByEventPage = () => {
  useScrollToTop();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [actors, setActors] = useState(initialActorsState);
  const [selectedActorEvent, setSelectedActorEvent] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [eventList, setEventList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);

  /* ===== Data loading ===== */

  useEffect(() => {
    if (eventId) {
      fetchActors();
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchActors = async () => {
    try {
      const data = await getAllActorsByEventId(eventId);

      const sortedActors = {
        Organisateur: [],
        ResponsableComite: [],
        Benevolontaire: [],
        ResponsableArbitrage: [],
        Notateur: [],
        Juge: [],
        Arbitre: [],
        CommissaireSportif: [],
      };

      if (data.Acteur && Array.isArray(data.Acteur)) {
        data.Acteur.forEach((actor) => {
          const poste = actor.ActeurEvent?.poste;
          switch (poste) {
            case "Organisateur":
              sortedActors.Organisateur.push(actor);
              break;
            case "Responsable Comité":
              sortedActors.ResponsableComite.push(actor);
              break;
            case "Bénévolontaire":
              sortedActors.Benevolontaire.push(actor);
              break;
            case "Responsable Arbitrage":
              sortedActors.ResponsableArbitrage.push(actor);
              break;
            case "Notateur":
              sortedActors.Notateur.push(actor);
              break;
            case "Juge":
              sortedActors.Juge.push(actor);
              break;
            case "Arbitre":
              sortedActors.Arbitre.push(actor);
              break;
            case "Commissaire Sportif":
              sortedActors.CommissaireSportif.push(actor);
              break;
            default:
              break;
          }
        });
      }

      // Tri par nom dans chaque section
      Object.keys(sortedActors).forEach((roleKey) => {
        sortedActors[roleKey].sort((a, b) =>
          `${a.nom} ${a.prenom}`
            .toLowerCase()
            .localeCompare(`${b.nom} ${b.prenom}`.toLowerCase())
        );
      });

      setEvent(data); // on garde ton comportement actuel
      setActors(sortedActors);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des acteurs de l'événement",
        error
      );
      toast.error("Erreur lors du chargement des acteurs de l'événement.");
    }
  };

  const fetchEvents = async () => {
    try {
      const events = await getAllEvents();
      setEventList(events || []);
      const index = (events || []).findIndex(
        (ev) => ev.id.toString() === eventId
      );
      setCurrentIndex(index);
    } catch (error) {
      console.error("Erreur lors du chargement des événements", error);
      toast.error("Erreur lors du chargement des événements.");
    }
  };

  /* ===== Interactions ===== */

  const openNoteModal = (actorEvent) => {
    setSelectedActorEvent(actorEvent);
    setIsNoteModalOpen(true);
  };

  const closeNoteModal = () => {
    setSelectedActorEvent(null);
    setIsNoteModalOpen(false);
  };

  const handleToggleValidation = async (acteurId) => {
    try {
      await toggleActeurValidation(acteurId, eventId);
      toast.success("Validation mise à jour avec succès.");
      fetchActors();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la validation", error);
      toast.error("Erreur lors de la mise à jour de la validation.");
    }
  };

  const handleNavigate = (direction) => {
    if (!eventList.length || currentIndex === -1) return;

    if (direction === "prev" && currentIndex > 0) {
      navigate(`/convocations-admin/${eventList[currentIndex - 1].id}`);
    } else if (direction === "next" && currentIndex < eventList.length - 1) {
      navigate(`/convocations-admin/${eventList[currentIndex + 1].id}`);
    }
  };

  /* ===== Stats ===== */

  const stats = useMemo(() => {
    const allActors = Object.values(actors).flat();
    const total = allActors.length;
    const validated = allActors.filter(
      (a) => a.ActeurEvent?.is_validate
    ).length;
    return {
      total,
      validated,
      pending: total - validated,
    };
  }, [actors]);

  /* ===== Rendu ===== */

  const renderValidationToggle = (acteurEvent) => {
    const isOk = !!acteurEvent?.is_validate;
    return (
      <button
        type="button"
        onClick={() => handleToggleValidation(acteurEvent.acteur_id)}
        className="mr-2 inline-flex items-center justify-center w-6 h-6 rounded-full border border-transparent hover:border-slate-300 bg-white shadow-sm"
        title={
          isOk
            ? "Marqué présent - cliquer pour décocher"
            : "À confirmer - cliquer pour marquer présent"
        }
      >
        {isOk ? (
          <FaCheck className="text-emerald-500" />
        ) : (
          <FaTimes className="text-rose-500" />
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      {/* TITRES GÉNÉRAUX */}
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.4em] text-gray-800 text-center dark:text-white mb-4">
        ADMINISTRATEURS
      </h2>
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest text-gray-800 text-center dark:text-white mb-6 font-montserrat uppercase">
        Détails de l&apos;Événement
      </h2>

      {/* CARTE ÉVÉNEMENT */}
      {event && (
        <div className="w-full max-w-[980px] mx-auto mb-5 rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/80 backdrop-blur">
          {/* Bandeau haut */}
          <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-sky-500 px-4 sm:px-6 py-3 text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] opacity-80">
                  Convocations – {formatDate(event.start)}
                </p>
                <p className="mt-1 text-base sm:text-lg font-semibold">
                  {event.titre}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs justify-start md:justify-end">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/95 text-slate-900 px-3 py-1 ring-1 ring-slate-200">
                  <FaUsers className="text-sky-500" />
                  <span className="font-semibold">
                    {stats.total} convoqué(s)
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 px-3 py-1 ring-1 ring-emerald-200">
                  Présents : {stats.validated}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-3 py-1 ring-1 ring-amber-200">
                  À confirmer : {stats.pending}
                </span>
              </div>
            </div>
          </div>

          {/* Contenu évènement */}
          <div className="flex flex-col md:flex-row gap-4 p-4 sm:p-5">
            <div className="flex-1 space-y-1 text-sm text-slate-800 dark:text-slate-100">
              {event.description && (
                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-200 mb-1">
                  <span className="font-semibold">Description :</span>{" "}
                  <span
                    dangerouslySetInnerHTML={{ __html: event.description }}
                  />
                </p>
              )}
              <p>
                <span className="font-semibold">Type :</span>{" "}
                {event.type_event || "-"}
              </p>
              <p>
                <span className="font-semibold">Niveau :</span>{" "}
                {event.level_event || "-"}
              </p>
              <p>
                <span className="font-semibold">Catégorie :</span>{" "}
                {event.cate_event || "-"}
              </p>
              <p>
                <span className="font-semibold">Lieu :</span>{" "}
                {event.lieu_event || "-"}
              </p>
              <p>
                <span className="font-semibold">Dates :</span>{" "}
                {formatDateRange(event.start, event.end)}
              </p>
              {event.horaire_event && (
                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-200">
                  <span className="font-semibold">Horaires :</span>{" "}
                  <span
                    dangerouslySetInnerHTML={{ __html: event.horaire_event }}
                  />
                </p>
              )}
            </div>
            <div className="w-full md:w-1/3 md:max-w-[260px]">
              <div className="relative w-full h-40 sm:h-48 md:h-full overflow-hidden rounded-xl ring-1 ring-slate-200 dark:ring-slate-600">
                <img
                  src={event.photo_url || "/img/default_event.jpg"}
                  alt={event.titre}
                  className="object-cover w-full h-full"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NAVIGATION ENTRE ÉVÉNEMENTS */}
      <div className="w-full max-w-[980px] mx-auto mb-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <Button
          onClick={() => handleNavigate("prev")}
          text="Événement précédent"
          className="items-center gap-2 btn-sm"
          icon={FaChevronLeft}
          disabled={currentIndex <= 0}
        />
        <Button
          onClick={() => navigate("/convocations-admin")}
          text="Retour à la liste des convocations"
          className="items-center gap-2 btn-sm"
        />
        <Button
          onClick={() => handleNavigate("next")}
          text="Événement suivant"
          className="items-center gap-2 btn-sm"
          icon={FaChevronRight}
          disabled={currentIndex < 0 || currentIndex >= eventList.length - 1}
        />
      </div>

      {/* INFOBULLE */}
      <InfoBubble title="Notations & Observations">
        <p>
          Cochez ou décochez la présence des acteurs sur cet événement. En
          cliquant sur le nom d’un acteur, vous pouvez ajouter une note et des
          observations. Les sections sont organisées par rôle (organisation,
          arbitrage, commissaires sportifs, bénévoles…).
        </p>
      </InfoBubble>

      {/* BOARD DES RÔLES */}
      <div className="bg-gray-100 dark:bg-gray-500/70 shadow-lg w-full max-w-[1280px] mx-auto mt-4 rounded-2xl">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROLE_SECTIONS.map((section) => {
              const list = actors[section.key] || [];
              return (
                <div
                  key={section.key}
                  className={`border rounded-2xl p-3 sm:p-4 ${section.tone} shadow-sm`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm sm:text-base font-semibold uppercase tracking-wide text-slate-800">
                      {section.label}
                    </h3>
                    <span className="inline-flex items-center justify-center text-[11px] px-2 py-0.5 rounded-full bg-white/80 text-slate-700 ring-1 ring-slate-200">
                      {list.length} acteur(s)
                    </span>
                  </div>
                  {section.description && (
                    <p className="text-[11px] text-slate-600 mb-2">
                      {section.description}
                    </p>
                  )}

                  {list.length > 0 ? (
                    <ul className="space-y-1.5 text-sm">
                      {list.map((actor) => (
                        <li
                          key={actor.ActeurEvent.acteur_id}
                          className="flex items-center justify-between bg-white/80 rounded-xl px-2 py-1.5 shadow-sm"
                        >
                          <div className="flex items-center">
                            {renderValidationToggle(actor.ActeurEvent)}
                            <button
                              type="button"
                              onClick={() =>
                                openNoteModal({
                                  ...actor.ActeurEvent,
                                  acteurLabel: `${actor.nom.toUpperCase()} ${
                                    actor.prenom
                                  }`,
                                })
                              }
                              className="text-xs sm:text-sm font-medium text-slate-800 hover:underline text-left"
                            >
                              {actor.nom.toUpperCase()} {actor.prenom}
                            </button>
                          </div>
                          <div className="flex items-center gap-1">
                            {actor.ActeurEvent?.note && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
                                Note
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-500 mt-1 italic">
                      Aucun acteur dans ce rôle.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={closeNoteModal}
        actorEvent={selectedActorEvent}
        fetchActors={fetchActors}
      />

      <ToastContainer />
    </div>
  );
};

export default ConvocationByEventPage;
